import React, {useEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';

import {useCurrentUser} from 'src/hooks/useCurrentUser';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {usePageTitle} from 'src/hooks/usePageTitle';
import {SsoStatus} from 'src/openapi/admin/models/SsoStatus';

import Loading from '@pb/kit-component-loading';
import {Stepper, StepperItem} from '@pb/kit-component-stepper';

import {DraftLockedByOtherUser} from './components/draft-locked-by-other-user/draft-locked-by-other-user';
import {CONNECTION_PATH, SETUP_COMPLETE_PATH, SSO_BASE_PATH, SSO_STEPS} from './constants';
import {useDraftConnection} from './hooks/use-draft-connection';
import {useExistingConnection} from './hooks/use-existing-connection';
import {SsoSettingsRouter} from './sso-settings-router';

import {container, contentArea, loadingContent, loadingIndicator, stepperBar} from './sso-settings.module.styl';

function getActiveStepFromPath(pathname: string): number {
    const segment = pathname.replace(SSO_BASE_PATH, '').replace(/^\//, '').split('/')[0];
    if (segment === '') {
        return 0;
    }
    return SSO_STEPS.findIndex(step => step.path === segment);
}

export const SsoSettings: React.FC = () => {
    usePageTitle('Client Portal: Set Up Single Sign-On (SSO)');
    const {loading: checkingConnection, connection, error} = useExistingConnection();
    const {
        loading: loadingDraft,
        draft,
        isOwnedByOtherUser,
        otherUser,
        otherUserStep,
        status,
    } = useDraftConnection();
    const location = useLocation();
    const {currentUser, currentUserStatus} = useCurrentUser();
    const {homeView} = useEventTracking();
    const homeViewTrackedRef = useRef(false);

    useEffect(() => {
        if (
            checkingConnection
            || loadingDraft
            || homeViewTrackedRef.current
            || currentUserStatus === 'idle'
            || currentUserStatus === 'loading'
        ) {
            return;
        }

        homeView({userRole: currentUser?.role?.name ?? currentUser?.role?.id ?? 'unknown'});
        homeViewTrackedRef.current = true;
    }, [checkingConnection, currentUser, currentUserStatus, homeView, loadingDraft]);

    if (checkingConnection || loadingDraft) {
        return (
            <div className={container}>
                <div className={`${contentArea} ${loadingContent}`}>
                    <div className={loadingIndicator}>
                        <Loading type="ICON"/>
                    </div>
                </div>
            </div>
        );
    }

    if (status !== SsoStatus.FINALIZED && isOwnedByOtherUser && otherUser != null && otherUserStep != null) {
        return (
            <div className={container}>
                <div className={contentArea}>
                    <DraftLockedByOtherUser/>
                </div>
            </div>
        );
    }

    const activeStep = getActiveStepFromPath(location.pathname);
    const currentSegment = location.pathname.replace(SSO_BASE_PATH + '/', '').split('/')[0];
    const showStepper = activeStep >= 0
        && currentSegment !== SETUP_COMPLETE_PATH
        && currentSegment !== CONNECTION_PATH;

    return (
        <div className={container}>
            {showStepper ? (
                <div className={stepperBar}>
                    <Stepper activeStep={activeStep}>
                        {SSO_STEPS.map((step, index) => (
                            <StepperItem
                                key={step.number}
                                caption={step.label}
                                completed={index < activeStep}
                            />
                        ))}
                    </Stepper>
                </div>
            ) : null}
            <div className={contentArea}>
                <SsoSettingsRouter connection={connection} error={error} draft={draft}/>
            </div>
        </div>
    );
};
